import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarCheck, Check, X, FileText, Image, ChartBar, Download, MagnifyingGlass, Warning, Receipt, HandWaving, Lightning } from '@phosphor-icons/react';
import AdminClearance from './Clearance';
import AdminReturnToWork from './ReturnToWork';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import useEmployeesMap from '@/hooks/useEmployeesMap';
import useEmployeeBadges from '@/hooks/useEmployeeBadges';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import ApprovalProgress, { leaveProgressSteps } from '@/components/ApprovalProgress';
import EmployeeName from '@/components/EmployeeName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function AdminLeaves() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, direction } = useLanguage();
  const { empNo } = useEmployeesMap();
  const { getBadge } = useEmployeeBadges();
  const isAr = language === 'ar';
  const isAdmin = user?.role === 'admin';
  const isGM = user?.role === 'general_manager' || !!user?.is_general_manager;
  // Finance Manager has READ-ONLY visibility into leaves — they need to
  // see every request and where it stands in the approval chain so they
  // can anticipate the eventual financial clearance, but they do NOT act
  // on the leave itself (their action belongs in the Clearance flow).
  const isFinanceMgrOnly = !!user?.is_finance_manager && !isAdmin && !isGM;
  // Balances + Clearances tabs are visible ONLY to Admin, GM and Finance
  // Manager. Department managers who reach this page (via their subordinate
  // approval flow) see the Requests tab only — they don't need employee
  // balance/clearance visibility.
  const canSeePrivilegedTabs = isAdmin || isGM || !!user?.is_finance_manager;
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // v1.5.91 — split the pending-leaves queue by leave type with a tab
  // row above the table. `all` keeps the old unified view; each other
  // tab filters to a single leave_type so finance / HR can clear each
  // category independently.
  const [typeTab, setTypeTab] = useState('all');

  // Requests tab — live search by employee name (Ar/En).
  const [reqSearch, setReqSearch] = useState('');

  // Balances tab state
  const [balances, setBalances] = useState([]);
  const [balLoading, setBalLoading] = useState(false);
  const [balSearch, setBalSearch] = useState('');
  const [balDept, setBalDept] = useState('all');
  const [activeTab, setActiveTab] = useState('requests');
  const [detailEmpId, setDetailEmpId] = useState(null);
  // v1.5.44 — Reconciliation dialog opened by clicking a warn/mismatch badge.
  const [reconcile, setReconcile] = useState(null); // { user_id, name, data, running }
  const [reconcileLoading, setReconcileLoading] = useState(false);

  const openReconcile = async (empId, name) => {
    setReconcile({ user_id: empId, name, data: null, running: false });
    setReconcileLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/leaves/admin/reconcile/${empId}`,
        { withCredentials: true },
      );
      setReconcile((prev) => (prev ? { ...prev, data } : prev));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
      setReconcile(null);
    } finally {
      setReconcileLoading(false);
    }
  };

  const runAutoFix = async () => {
    if (!reconcile) return;
    setReconcile((prev) => ({ ...prev, running: true }));
    try {
      const { data } = await axios.post(
        `${API}/leaves/admin/reconcile/${reconcile.user_id}`, {},
        { withCredentials: true },
      );
      toast.success(
        isAr
          ? `تم إصلاح ${data.leaves_fixed} إجازة و ${data.cash_outs_fixed} صرف نقدي (${data.days_consumed_total} يوم)`
          : `Fixed ${data.leaves_fixed} leaves + ${data.cash_outs_fixed} cash-outs (${data.days_consumed_total} days)`,
        { duration: 6000 },
      );
      setReconcile(null);
      await fetchBalances();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      setReconcile((prev) => ({ ...prev, running: false }));
    }
  };

  useEffect(() => {
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (activeTab === 'balances' && canSeePrivilegedTabs && balances.length === 0) fetchBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Always fetch balances once on mount so the negative-balance warning
  // can render next to leave-request rows (not only inside the Balances tab).
  // Skipped for department managers — they don't have permission and
  // don't need this warning either.
  useEffect(() => {
    if (canSeePrivilegedTabs && balances.length === 0) fetchBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overageByUserId = balances.reduce((acc, b) => {
    if ((b.overage_days || 0) > 0) acc[b.user_id] = b.overage_days;
    return acc;
  }, {});

  const fetchBalances = async () => {
    setBalLoading(true);
    try {
      const { data } = await axios.get(`${API}/leaves/admin/balances`, { withCredentials: true });
      setBalances(data);
    } catch (e) {
      console.error('Failed to fetch balances:', e);
    } finally {
      setBalLoading(false);
    }
  };

  const exportBalancesCSV = () => {
    const headers = isAr
      ? ['الاسم', 'القسم', 'المنصب', 'السياسة', 'المستحق', 'المستخدم', 'المعلق', 'المنتهي', 'المتبقي', 'مدة الخدمة', 'تاريخ التعيين']
      : ['Name', 'Department', 'Position', 'Policy', 'Entitlement', 'Used', 'Pending', 'Expired', 'Remaining', 'Years of service', 'Hire date'];
    const rows = filteredBalances.map(b => [
      b.name, b.department, b.position, `${b.annual_days_policy} d/y`,
      b.entitlement, b.used_days, b.pending_days ?? 0, b.expired_days, b.remaining_days,
      b.years_of_service, b.hire_date,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave_balances_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Compute filtered balances + unique department list
  const balDepartments = Array.from(new Set(balances.map(b => b.department).filter(Boolean)));
  const filteredBalances = balances.filter(b => {
    if (balDept !== 'all' && b.department !== balDept) return false;
    if (balSearch) {
      const q = balSearch.toLowerCase();
      return (b.name || '').toLowerCase().includes(q)
        || (b.name_en || '').toLowerCase().includes(q)
        || (b.department || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Detail rows for the selected employee
  const detailRows = detailEmpId ? leaves.filter(l => l.user_id === detailEmpId) : [];

  // Live search for the Requests tab — matches Arabic name, English name
  // and employee number. Applied uniformly to the type tab counts +
  // mobile list + desktop list so everything stays in sync.
  const q = (reqSearch || '').trim().toLowerCase();
  const base = q
    ? leaves.filter(l => {
        const n1 = (l.user_name || '').toLowerCase();
        const n2 = (l.user_name_en || '').toLowerCase();
        const no = String(l.employee_number || '').toLowerCase();
        return n1.includes(q) || n2.includes(q) || no.includes(q);
      })
    : leaves;

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? `${API}/leaves` : `${API}/leaves?status=${filter}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setLeaves(data);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (leave) => {
    try {
      // Route to the right endpoint based on the current step in the chain
      let url;
      if (leave.status === 'pending_gm') {
        url = `${API}/leaves/${leave.id}/gm-approve`;
      } else if (leave.status === 'pending_manager') {
        url = `${API}/leaves/${leave.id}/manager-approve`;
      } else {
        url = `${API}/leaves/${leave.id}/approve`;
      }
      await axios.put(url, {}, { withCredentials: true });
      fetchLeaves();
    } catch (error) {
      console.error('Failed to approve leave:', error);
    }
  };

  const handleReject = async (leave) => {
    try {
      let url;
      // System admin has override authority — reject at any pending
      // stage via the admin endpoint (which now accepts pending_manager,
      // pending_gm, and pending_admin). Everyone else uses the
      // stage-specific endpoint so authorization is enforced.
      if (isAdmin) {
        url = `${API}/leaves/${leave.id}/reject`;
      } else if (leave.status === 'pending_gm') {
        url = `${API}/leaves/${leave.id}/gm-reject`;
      } else if (leave.status === 'pending_manager') {
        url = `${API}/leaves/${leave.id}/manager-reject`;
      } else {
        url = `${API}/leaves/${leave.id}/reject`;
      }
      await axios.put(url, {}, { withCredentials: true });
      fetchLeaves();
    } catch (error) {
      console.error('Failed to reject leave:', error);
    }
  };

  // Department manager acts on `pending_manager` rows. We rely on the
  // backend to enforce authorization on approve/reject — if the backend
  // returned the row via GET /leaves, the current user is allowed to see
  // (and, for pending_manager rows of subordinates, act on) it.
  const isDeptMgr = !!user?.is_manager || !!user?.is_department_manager;
  // Decide whether the current logged-in user can act on this row
  const canActOnLeave = (leave) => {
    // System admin can OVERRIDE-REJECT any pending request (see the
    // /reject endpoint) even if it's still sitting on a manager or GM.
    if (isAdmin && (leave.status === 'pending' || leave.status === 'pending_manager'
                    || leave.status === 'pending_gm' || leave.status === 'pending_admin')) {
      return true;
    }
    if (leave.status === 'pending_gm') return isGM || isAdmin;
    if (leave.status === 'pending_manager' || leave.status === 'pending') {
      // Department managers act on their subordinates' pending requests.
      // Never allow self-approval on your own request.
      return isDeptMgr && leave.user_id !== user?.id;
    }
    return false;
  };

  const getStatusLabel = (status) => {
    if (isAr) {
      const map = {
        pending: 'بانتظار',
        pending_manager: 'بانتظار مدير القسم',
        pending_gm: 'بانتظار المدير العام',
        pending_admin: 'بانتظار مدير النظام',
        approved: 'معتمد',
        rejected: 'مرفوض',
      };
      return map[status] || status;
    }
    const map = {
      pending: 'Pending',
      pending_manager: 'Awaiting Dept. Manager',
      pending_gm: 'Awaiting General Manager',
      pending_admin: 'Awaiting System Admin',
      approved: 'Approved',
      rejected: 'Rejected',
    };
    return map[status] || status;
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      pending_manager: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      pending_gm: 'bg-orange-100 text-orange-700 border-orange-200',
      pending_admin: 'bg-blue-100 text-blue-700 border-blue-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200'
    };
    return variants[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getLeaveTypeLabel = (type) => {
    const types = {
      annual: t('annual_leave'),
      sick: t('sick_leave'),
      unpaid: t('unpaid_leave')
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="admin-leaves-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-2">
            <CalendarCheck size={28} weight="duotone" />
            {t('leaves')}
          </h1>
          {isAdmin && (
            <Button
              onClick={() => navigate('/admin/opening-balance')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm gap-2"
              data-testid="open-opening-balance-btn"
            >
              <Lightning size={18} weight="duotone" />
              {isAr ? 'الرصيد الافتتاحي للموظفين' : 'Opening Leave Balance'}
            </Button>
          )}
          {/* The legacy "Fix reversed dates" button used to live here. It
              was a one-shot data-repair utility that's no longer needed:
              new submissions are blocked by the backdating + reversed-date
              guards introduced 2026-02. The endpoint
              POST /api/leaves/admin/fix-reversed-dates is still
              available for manual recovery via curl if ever required. */}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} dir={direction}>
          <TabsList className="bg-white dark:bg-slate-800 border border-[#655dc6]/10 dark:border-slate-700 w-full sm:w-auto overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="requests" data-testid="tab-leaves-requests" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0">
              <CalendarCheck size={18} weight="duotone" />
              {isAr ? 'طلبات الإجازة' : 'Leave Requests'}
            </TabsTrigger>
            {canSeePrivilegedTabs && (
              <>
                <TabsTrigger value="balances" data-testid="tab-leaves-balances" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0">
                  <ChartBar size={18} weight="duotone" />
                  {isAr ? 'أرصدة الموظفين' : 'Employee Balances'}
                </TabsTrigger>
                <TabsTrigger value="clearances" data-testid="tab-leaves-clearances" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0">
                  <Receipt size={18} weight="duotone" />
                  {isAr ? 'المخالصات' : 'Clearances'}
                </TabsTrigger>
              </>
            )}
            {isAdmin && (
              <TabsTrigger value="return-to-work" data-testid="tab-leaves-rtw" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0">
                <HandWaving size={18} weight="duotone" />
                {isAr ? 'مباشرة العمل' : 'Return to Work'}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="requests" className="space-y-4 mt-4">
            {isFinanceMgrOnly && (
              <div
                className="rounded-lg border border-[#C4BFF0] bg-[#F8F7FC] px-4 py-3 text-sm text-[#655dc6] flex items-start gap-2"
                data-testid="finance-readonly-banner"
              >
                <Receipt size={18} weight="duotone" className="mt-0.5 shrink-0" />
                <span>
                  {isAr
                    ? 'هذه شاشة عرض للاطلاع فقط — تُمكّنك من متابعة طلبات الإجازة ومعرفة موقع كل طلب في مسار الموافقات. لا يُطلب منك أي إجراء على طلب الإجازة، الإجراء المالي يبدأ لاحقاً عند مرحلة المخالصة المالية.'
                    : 'Read-only view — track every leave request and where it stands in the approval workflow. No action is required from you on the leave itself; the financial action begins later at the Clearance step.'}
                </span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
              <div className="relative w-full sm:w-[260px]">
                <MagnifyingGlass size={16} weight="bold" className="absolute top-1/2 -translate-y-1/2 start-3 text-[#655dc6]/60 pointer-events-none" />
                <Input
                  value={reqSearch}
                  onChange={(e) => setReqSearch(e.target.value)}
                  placeholder={isAr ? 'بحث بالاسم...' : 'Search by name...'}
                  className="ps-9 bg-white border-[#C4BFF0] text-[#655dc6] placeholder:text-[#655dc6]/40 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                  data-testid="leaves-request-search"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger
                  className="w-full sm:w-[180px] bg-[#E8E6F5] border-[#C4BFF0] text-[#655dc6] dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                  data-testid="status-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isAr ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="pending">{t('pending')}</SelectItem>
                  <SelectItem value="approved">{t('approved')}</SelectItem>
                  <SelectItem value="rejected">{t('rejected')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <CalendarCheck size={22} weight="duotone" className="text-[#655dc6]" />
              {t('pending_leaves')}: {leaves.filter(l => ['pending', 'pending_manager', 'pending_gm', 'pending_admin'].includes(l.status)).length}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Type tabs — split the queue by leave_type with live counts.
                Preserves the status Select (above) + the admin workflow;
                tabs just narrow which leave_type you're reviewing. */}
            {(() => {
              const pendingOf = (type) =>
                base.filter(l => (type === 'all' || l.leave_type === type)
                  && ['pending', 'pending_manager', 'pending_gm', 'pending_admin'].includes(l.status)).length;
              const totalOf = (type) =>
                base.filter(l => type === 'all' || l.leave_type === type).length;
              const tabCfg = [
                { key: 'all',    labelAr: 'الكل',          labelEn: 'All' },
                { key: 'annual', labelAr: 'سنوية',         labelEn: 'Annual' },
                { key: 'sick',   labelAr: 'مرضية',         labelEn: 'Sick' },
                { key: 'unpaid', labelAr: 'بدون راتب',    labelEn: 'Unpaid' },
              ];
              return (
                <div className="flex flex-wrap items-center gap-2 mb-4 border-b border-[#C4BFF0] pb-3" data-testid="leave-type-tabs">
                  {tabCfg.map(({ key, labelAr, labelEn }) => {
                    const total = totalOf(key);
                    const pend = pendingOf(key);
                    const active = typeTab === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setTypeTab(key)}
                        className={`inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium transition-colors ${active ? 'bg-[#655dc6] text-white shadow-sm' : 'bg-[#F3F1FB] text-[#655dc6] hover:bg-[#E8E6F5]'}`}
                        data-testid={`leave-type-tab-${key}`}
                      >
                        <span>{isAr ? labelAr : labelEn}</span>
                        {pend > 0 && (
                          <span className={`text-[11px] px-1.5 rounded-full font-bold ${active ? 'bg-white/25 text-white' : 'bg-amber-500/90 text-white'}`} title={isAr ? 'بانتظار اعتماد' : 'Pending approval'}>
                            {pend}
                          </span>
                        )}
                        <span className={`text-[11px] px-1.5 rounded-full ${active ? 'bg-white/15 text-white/90' : 'bg-[#655dc6]/15 text-[#655dc6]/80'}`} title={isAr ? 'إجمالي الطلبات' : 'Total requests'}>
                          {total}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : (
              <>
              {/* Mobile Card List (<640px) */}
              <div className="sm:hidden space-y-3" data-testid="leaves-mobile-list">
                {(() => {
                  const visible = base.filter(l => typeTab === 'all' || l.leave_type === typeTab);
                  if (visible.length === 0) {
                    return (
                      <p className="text-center py-8 text-[#655dc6]/70 text-sm">
                        {q
                          ? (isAr ? `لا توجد نتائج مطابقة لـ "${reqSearch}"` : `No results matching "${reqSearch}"`)
                          : typeTab !== 'all'
                          ? (isAr ? `لا توجد طلبات ${typeTab === 'annual' ? 'سنوية' : typeTab === 'sick' ? 'مرضية' : 'بدون راتب'} حالياً` : 'No requests in this category')
                          : t('no_data')}
                      </p>
                    );
                  }
                  return visible.map((leave) => {
                    const showProgress =
                      isFinanceMgrOnly ||
                      (leave.status !== 'approved' && leave.status !== 'rejected');
                    return (
                      <div
                        key={leave.id}
                        data-testid={`leave-card-${leave.id}`}
                        className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                      >
                        {/* Header: employee + status */}
                        <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-[#262263] dark:text-slate-100 text-sm">
                              <EmployeeName
                                name={leave.user_name}
                                nameEn={leave.user_name_en}
                                badgeData={getBadge(leave.user_id)}
                                badgeLanguage={language}
                              />
                            </div>
                            <div className="text-[11px] text-[#655dc6]/70 dark:text-slate-400 font-mono mt-0.5">
                              #{leave.employee_number || empNo(leave.user_id)}
                            </div>
                            {overageByUserId[leave.user_id] > 0 && (
                              <span
                                className="mt-1 inline-flex items-center gap-1 text-[10px] bg-red-100 text-red-700 border border-red-300 px-1.5 py-0.5 rounded-full font-semibold"
                                data-testid={`leave-negative-badge-mobile-${leave.id}`}
                              >
                                ⚠ رصيد سالب −{overageByUserId[leave.user_id]}
                              </span>
                            )}
                          </div>
                          <Badge className={`${getStatusBadge(leave.status)} shrink-0 text-[11px]`} data-testid={`leave-status-mobile-${leave.id}`}>
                            {getStatusLabel(leave.status)}
                          </Badge>
                        </div>

                        {/* Metadata grid */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          <div>
                            <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('leave_type')}</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{getLeaveTypeLabel(leave.leave_type)}</span>
                          </div>
                          <div>
                            <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{isAr ? 'الوجهة' : 'Destination'}</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                              {leave.leave_type === 'annual' && leave.destination_city ? leave.destination_city : '—'}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('start_date')}</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{leave.start_date}</span>
                          </div>
                          <div>
                            <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('end_date')}</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{leave.end_date}</span>
                          </div>
                          {leave.reason && (
                            <div className="col-span-2">
                              <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('reason')}</span>
                              <span className="text-slate-700 dark:text-slate-300 line-clamp-2">{leave.reason}</span>
                            </div>
                          )}
                          {leave.attachment_url && (
                            <div className="col-span-2">
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${leave.attachment_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[#655dc6] hover:underline text-xs"
                                data-testid={`attachment-link-mobile-${leave.id}`}
                              >
                                {leave.attachment_url.endsWith('.pdf') ? <FileText size={14} /> : <Image size={14} />}
                                {t('view')} {t('attachment')}
                              </a>
                            </div>
                          )}
                        </div>

                        {showProgress && (
                          <div className="mb-3 -mx-1 overflow-x-auto no-scrollbar">
                            <ApprovalProgress steps={leaveProgressSteps(leave)} />
                          </div>
                        )}

                        {/* Actions */}
                        {canActOnLeave(leave) && (
                          <div className="pt-3 flex items-center gap-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                            <Button
                              size="sm"
                              onClick={() => handleReject(leave)}
                              variant="outline"
                              className="flex-1 min-h-[44px] border-red-200 text-red-600 hover:bg-red-50 gap-1.5"
                              data-testid={`reject-btn-mobile-${leave.id}`}
                            >
                              <X size={16} weight="bold" />
                              {t('reject')}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(leave)}
                              className="flex-1 min-h-[44px] bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                              data-testid={`approve-btn-mobile-${leave.id}`}
                            >
                              <Check size={16} weight="bold" />
                              {t('approve')}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Desktop Table (>=640px) */}
              <div className="hidden sm:block overflow-x-auto">
                <Table data-testid="leaves-table">
                  <TableHeader>
                    <TableRow className="border-[#C4BFF0]">
                      <TableHead className="text-start">{t('employee_number')}</TableHead>
                      <TableHead className="text-start">{t('employee_name')}</TableHead>
                      <TableHead className="text-start">{t('leave_type')}</TableHead>
                      <TableHead className="text-start">{t('start_date')}</TableHead>
                      <TableHead className="text-start">{t('end_date')}</TableHead>
                      <TableHead className="text-start">{isAr ? 'الوجهة' : 'Destination'}</TableHead>
                      <TableHead className="text-start">{t('reason')}</TableHead>
                      <TableHead className="text-start">{t('attachment')}</TableHead>
                      <TableHead className="text-start">{t('status')}</TableHead>
                      <TableHead className="text-start">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const visible = base.filter(l => typeTab === 'all' || l.leave_type === typeTab);
                      if (visible.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-8 text-[#655dc6]/70">
                              {q
                                ? (isAr ? `لا توجد نتائج مطابقة لـ "${reqSearch}"` : `No results matching "${reqSearch}"`)
                                : typeTab !== 'all'
                                ? (isAr ? `لا توجد طلبات ${typeTab === 'annual' ? 'سنوية' : typeTab === 'sick' ? 'مرضية' : 'بدون راتب'} حالياً` : 'No requests in this category')
                                : t('no_data')}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return visible.map((leave) => {
                        // Show the approval timeline for in-flight requests
                        // OR ALWAYS for the Finance Manager — they need
                        // full visibility (incl. approved/rejected) to
                        // anticipate the upcoming financial clearance.
                        const showProgress =
                          isFinanceMgrOnly ||
                          (leave.status !== 'approved' && leave.status !== 'rejected');
                        return (
                        <React.Fragment key={leave.id}>
                        <TableRow className="border-[#C4BFF0]" data-testid={`leave-row-${leave.id}`}>
                          <TableCell className="text-[#655dc6]/80 font-mono text-xs" data-testid={`leave-empno-${leave.id}`}>{leave.employee_number || empNo(leave.user_id)}</TableCell>
                          <TableCell className="font-medium text-[#655dc6]">
                            <EmployeeName
                              name={leave.user_name}
                              nameEn={leave.user_name_en}
                              badgeData={getBadge(leave.user_id)}
                              badgeLanguage={language}
                              extra={overageByUserId[leave.user_id] > 0 ? (
                                <span
                                  className="mt-1 inline-flex items-center gap-1 text-[10px] bg-red-100 text-red-700 border border-red-300 px-1.5 py-0.5 rounded-full font-semibold"
                                  title={`الموظف تجاوز رصيده السنوي بمقدار ${overageByUserId[leave.user_id]} يوم — راجِع قبل الاعتماد`}
                                  data-testid={`leave-negative-badge-${leave.id}`}
                                >
                                  ⚠ رصيد سالب −{overageByUserId[leave.user_id]}
                                </span>
                              ) : null}
                            />
                          </TableCell>
                          <TableCell className="text-[#655dc6]/70">{getLeaveTypeLabel(leave.leave_type)}</TableCell>
                          <TableCell className="text-[#655dc6]/70">{leave.start_date}</TableCell>
                          <TableCell className="text-[#655dc6]/70">{leave.end_date}</TableCell>
                          {/* Destination — annual leaves only; other types have no destination concept. */}
                          <TableCell className="text-[#655dc6]/70 max-w-[140px] truncate" data-testid={`leave-destination-${leave.id}`}>
                            {leave.leave_type === 'annual' && leave.destination_city
                              ? leave.destination_city
                              : <span className="text-[#655dc6]/30">-</span>}
                          </TableCell>
                          <TableCell className="text-[#655dc6]/70 max-w-[200px] truncate">
                            {leave.reason || '-'}
                          </TableCell>
                          <TableCell>
                            {leave.attachment_url ? (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${leave.attachment_url}`} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[#655dc6] hover:underline text-sm" data-testid={`attachment-link-${leave.id}`}>
                                {leave.attachment_url.endsWith('.pdf') ? <FileText size={16} /> : <Image size={16} />}
                                {t('view')}
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusBadge(leave.status)}>
                              {getStatusLabel(leave.status)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {canActOnLeave(leave) ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleApprove(leave)}
                                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`approve-btn-${leave.id}`}
                                >
                                  <Check size={18} weight="bold" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleReject(leave)}
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`reject-btn-${leave.id}`}
                                >
                                  <X size={18} weight="bold" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-[#655dc6]/50">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                        {showProgress && (
                          <TableRow className="border-[#C4BFF0] bg-[#F8F7FC]">
                            <TableCell colSpan={10} className="py-3 px-4">
                              <ApprovalProgress steps={leaveProgressSteps(leave)} />
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* ===== BALANCES TAB ===== */}
          <TabsContent value="balances" className="space-y-4 mt-4">
            <Card className="border border-[#C4BFF0]">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
                    <ChartBar size={22} weight="duotone" />
                    {isAr ? `أرصدة الموظفين (${filteredBalances.length})` : `Employee Balances (${filteredBalances.length})`}
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <MagnifyingGlass size={16} className="absolute top-1/2 -translate-y-1/2 start-2 text-[#655dc6]/60" />
                      <Input
                        value={balSearch}
                        onChange={(e) => setBalSearch(e.target.value)}
                        placeholder={isAr ? 'بحث بالاسم...' : 'Search by name...'}
                        className="ps-8 w-56 border-[#C4BFF0]"
                        data-testid="balance-search"
                      />
                    </div>
                    <Select value={balDept} onValueChange={setBalDept}>
                      <SelectTrigger className="w-44 bg-[#E8E6F5] border-[#C4BFF0]" data-testid="balance-dept-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isAr ? 'كل الأقسام' : 'All departments'}</SelectItem>
                        {balDepartments.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      onClick={exportBalancesCSV}
                      disabled={filteredBalances.length === 0}
                      className="gap-2 border-[#C4BFF0] text-[#655dc6] hover:bg-[#E8E6F5]"
                      data-testid="export-balances-btn"
                    >
                      <Download size={16} />
                      {isAr ? 'تصدير Excel' : 'Export Excel'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {balLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="balances-table">
                      <TableHeader>
                        <TableRow className="border-[#C4BFF0]">
                          <TableHead className="text-start">{t('employee_number')}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'القسم' : 'Department'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'السياسة' : 'Policy'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'المستحق' : 'Entitlement'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'المستخدم' : 'Used'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'المعلق' : 'Pending'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'المنتهي' : 'Expired'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'المتبقي' : 'Remaining'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'مدة الخدمة' : 'Service'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredBalances.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-8 text-[#655dc6]/70">
                              {isAr ? 'لا يوجد موظفون' : 'No employees'}
                            </TableCell>
                          </TableRow>
                        ) : filteredBalances.map(b => {
                          const hasExpired = b.expired_days > 0;
                          return (
                            <TableRow
                              key={b.user_id}
                              className={`border-[#C4BFF0] cursor-pointer hover:bg-[#F8F7FC] ${hasExpired ? 'bg-red-50/40' : ''}`}
                              onClick={() => setDetailEmpId(b.user_id === detailEmpId ? null : b.user_id)}
                              data-testid={`balance-row-${b.user_id}`}
                            >
                              <TableCell className="text-[#655dc6]/80 font-mono text-xs">{b.employee_number || empNo(b.user_id)}</TableCell>
                              <TableCell className="font-semibold text-[#655dc6]">
                                <div className="flex items-center gap-2">
                                  {hasExpired && (
                                    <Warning size={16} weight="fill" className="text-red-600" />
                                  )}
                                  {/* v1.5.43 — Balance-health badge.
                                     Shows when the two independent formulas
                                     (raw − history) and (FIFO slices) don't
                                     agree, so HR can reconcile slices before
                                     the employee complains. */}
                                  {b.balance_health && b.balance_health.level !== 'ok' && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openReconcile(b.user_id, b.name); }}
                                      title={isAr
                                        ? `اضغط لعرض تفاصيل التباين (${b.balance_health.gap_days} يوم) وإصلاحه تلقائياً`
                                        : `Click for discrepancy detail (${b.balance_health.gap_days} days) & auto-fix`}
                                      className={
                                        b.balance_health.level === 'mismatch'
                                          ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-red-100 text-red-700 border border-red-300 hover:bg-red-200 focus:ring-2 focus:ring-red-400 focus:outline-none transition'
                                          : 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200 focus:ring-2 focus:ring-orange-400 focus:outline-none transition'
                                      }
                                      data-testid={`balance-health-${b.user_id}`}
                                    >
                                      <Warning size={11} weight="fill" />
                                      {b.balance_health.gap_days}{isAr ? 'ي' : 'd'}
                                    </button>
                                  )}
                                  <EmployeeName
                                    name={b.name}
                                    nameEn={b.name_en}
                                    badgeData={getBadge(b.user_id)}
                                    badgeLanguage={language}
                                    extra={b.position ? (
                                      <div className="text-[10px] text-[#655dc6]/60">{b.position}</div>
                                    ) : null}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="text-[#655dc6]/80">{b.department || '-'}</TableCell>
                              <TableCell>
                                <Badge className={b.annual_days_policy === 30 ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}>
                                  {b.annual_days_policy} {isAr ? 'يوم/سنة' : 'd/y'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center font-mono">
                                <span
                                  title={
                                    b.entitlement > (b.annual_days_policy || 0) * 1.2
                                      ? (isAr
                                          ? `إجمالي ما اكتسبه الموظف منذ تعيينه (${b.years_of_service} سنة × ${b.annual_days_policy} يوم). المتبقي القابل للاستخدام أو الصرف النقدي = ${b.remaining_days} يوم.`
                                          : `Total accrued since hire (${b.years_of_service}y × ${b.annual_days_policy}d). Currently available = ${b.remaining_days} days.`)
                                      : (isAr
                                          ? `الاستحقاق النسبي حسب مدة الخدمة (${b.years_of_service} سنة).`
                                          : `Prorated entitlement for ${b.years_of_service}y of service.`)
                                  }
                                  className="cursor-help underline decoration-dotted decoration-[#655dc6]/40"
                                >
                                  {b.entitlement}
                                </span>
                              </TableCell>
                              <TableCell className="text-center font-mono text-red-700">
                                <span
                                  title={(() => {
                                    const leaves = Math.max(0, (b.used_days || 0) - (b.cash_out_days || 0));
                                    const cash = b.cash_out_days || 0;
                                    return isAr
                                      ? `إجازات: ${leaves} يوم · صرف نقدي: ${cash} يوم`
                                      : `Leaves: ${leaves}d · Cash-out: ${cash}d`;
                                  })()}
                                  className="cursor-help"
                                  data-testid={`used-days-${b.user_id}`}
                                >
                                  {b.used_days}
                                </span>
                                {b.cash_out_days > 0 && (
                                  <div className="text-[9px] text-blue-600 font-sans mt-0.5">
                                    {isAr ? `منها ${b.cash_out_days} ن. صرف` : `incl. ${b.cash_out_days}d cash`}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-center font-mono text-amber-700">{b.pending_days ?? 0}</TableCell>
                              <TableCell className={`text-center font-mono ${hasExpired ? 'text-red-600 font-bold' : 'text-[#655dc6]/40'}`}>
                                {b.expired_days}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`inline-block px-2 py-0.5 rounded-md font-bold ${b.remaining_days > 0 ? 'bg-[#655dc6] text-white' : 'bg-red-100 text-red-700'}`}>
                                  {b.remaining_days}
                                </span>
                              </TableCell>
                              <TableCell className="text-center text-xs text-[#655dc6]/70">
                                {b.years_of_service} {isAr ? 'سنة' : 'y'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    {/* Expand: per-employee leave history */}
                    {detailEmpId && (
                      <div className="mt-6 border border-[#C4BFF0] rounded-lg p-4 bg-[#F8F7FC]" data-testid="employee-leave-detail">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-[#655dc6]">
                            {isAr ? 'سجل الإجازات للموظف' : 'Employee Leave History'}
                            {' — '}
                            {balances.find(b => b.user_id === detailEmpId)?.name}
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setDetailEmpId(null)} className="text-[#655dc6]">
                            <X size={16} />
                          </Button>
                        </div>
                        {detailRows.length === 0 ? (
                          <p className="text-xs text-[#655dc6]/60 text-center py-4">
                            {isAr ? 'لا توجد طلبات إجازة لهذا الموظف' : 'No leave requests for this employee'}
                          </p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow className="border-[#C4BFF0]">
                                <TableHead className="text-start text-xs">{isAr ? 'النوع' : 'Type'}</TableHead>
                                <TableHead className="text-start text-xs">{isAr ? 'من' : 'From'}</TableHead>
                                <TableHead className="text-start text-xs">{isAr ? 'إلى' : 'To'}</TableHead>
                                <TableHead className="text-start text-xs">{isAr ? 'السبب' : 'Reason'}</TableHead>
                                <TableHead className="text-start text-xs">{isAr ? 'الحالة' : 'Status'}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detailRows.map(lv => (
                                <TableRow key={lv.id} className="border-[#C4BFF0] text-xs">
                                  <TableCell>{getLeaveTypeLabel(lv.leave_type)}</TableCell>
                                  <TableCell>{lv.start_date}</TableCell>
                                  <TableCell>{lv.end_date}</TableCell>
                                  <TableCell className="max-w-xs truncate">{lv.reason || '-'}</TableCell>
                                  <TableCell>
                                    <Badge className={getStatusBadge(lv.status)}>{getStatusLabel(lv.status)}</Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clearances" className="mt-4">
            <AdminClearance embed />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="return-to-work" className="mt-4">
              <AdminReturnToWork embed />
            </TabsContent>
          )}
        </Tabs>

        {/* v1.5.44 — Reconciliation dialog opened by clicking a balance-health badge. */}
        <Dialog open={!!reconcile} onOpenChange={(o) => { if (!o) setReconcile(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="reconcile-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">
                {isAr ? 'تفاصيل التباين في الرصيد' : 'Balance Discrepancy Detail'}
                {reconcile?.name && <span className="text-sm text-[#655dc6]/60 ms-2">— {reconcile.name}</span>}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-3 text-sm">
              {reconcileLoading || !reconcile?.data ? (
                <div className="text-center text-[#655dc6]/60 py-8">{isAr ? 'جاري التحميل…' : 'Loading…'}</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 rounded bg-emerald-50 border border-emerald-200">
                      <div className="text-emerald-700/70">{isAr ? 'FIFO المتبقي' : 'FIFO remaining'}</div>
                      <div className="font-mono font-bold text-emerald-800">{reconcile.data.fifo.total_days_remaining}</div>
                    </div>
                    <div className="p-2 rounded bg-orange-50 border border-orange-200">
                      <div className="text-orange-700/70">{isAr ? 'إجازات لم تُطبَّق' : 'Unapplied leaves'}</div>
                      <div className="font-mono font-bold text-orange-800">{reconcile.data.unapplied_leave_days}</div>
                    </div>
                    <div className="p-2 rounded bg-orange-50 border border-orange-200">
                      <div className="text-orange-700/70">{isAr ? 'صرف نقدي لم يُطبَّق' : 'Unapplied cash-outs'}</div>
                      <div className="font-mono font-bold text-orange-800">{reconcile.data.unapplied_cash_days}</div>
                    </div>
                  </div>
                  {reconcile.data.leaves.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-[#655dc6] mb-1">{isAr ? 'الإجازات المعتمدة' : 'Approved Leaves'}</div>
                      <div className="max-h-40 overflow-y-auto border border-[#C4BFF0]/60 rounded">
                        <table className="w-full text-[11px]">
                          <thead className="bg-[#F5F3FF] sticky top-0">
                            <tr><th className="text-start px-2 py-1">{isAr ? 'من' : 'From'}</th><th className="text-start px-2 py-1">{isAr ? 'إلى' : 'To'}</th><th className="text-end px-2 py-1">{isAr ? 'أيام' : 'Days'}</th><th className="text-center px-2 py-1">{isAr ? 'طُبِّق' : 'Applied'}</th></tr>
                          </thead>
                          <tbody>
                            {reconcile.data.leaves.map(l => (
                              <tr key={l.id} className={`border-b border-[#F0EEF7] ${!l.applied_to_slices ? 'bg-orange-50/60' : ''}`}>
                                <td className="px-2 py-1 font-mono" dir="ltr">{l.start_date}</td>
                                <td className="px-2 py-1 font-mono" dir="ltr">{l.end_date}</td>
                                <td className="px-2 py-1 text-end font-mono">{l.days}</td>
                                <td className="px-2 py-1 text-center">{l.applied_to_slices ? <Check size={12} className="text-emerald-600 inline"/> : <Warning size={12} className="text-orange-600 inline"/>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {reconcile.data.cash_outs.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-[#655dc6] mb-1">{isAr ? 'الصرف النقدي الموقَّع' : 'Signed Cash-outs'}</div>
                      <div className="max-h-40 overflow-y-auto border border-[#C4BFF0]/60 rounded">
                        <table className="w-full text-[11px]">
                          <thead className="bg-[#F5F3FF] sticky top-0">
                            <tr><th className="text-start px-2 py-1">{isAr ? 'التاريخ' : 'Date'}</th><th className="text-end px-2 py-1">{isAr ? 'أيام' : 'Days'}</th><th className="text-center px-2 py-1">{isAr ? 'طُبِّق' : 'Applied'}</th></tr>
                          </thead>
                          <tbody>
                            {reconcile.data.cash_outs.map(c => (
                              <tr key={c.id} className={`border-b border-[#F0EEF7] ${!c.applied_to_slices ? 'bg-orange-50/60' : ''}`}>
                                <td className="px-2 py-1 font-mono" dir="ltr">{c.signed_at}</td>
                                <td className="px-2 py-1 text-end font-mono">{c.days}</td>
                                <td className="px-2 py-1 text-center">{c.applied_to_slices ? <Check size={12} className="text-emerald-600 inline"/> : <Warning size={12} className="text-orange-600 inline"/>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter className="border-t border-[#C4BFF0]/40 pt-3">
              <Button variant="outline" onClick={() => setReconcile(null)}>{isAr ? 'إغلاق' : 'Close'}</Button>
              {reconcile?.data?.reconcilable && (
                <Button
                  onClick={runAutoFix}
                  disabled={reconcile?.running}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="reconcile-fix-btn"
                >
                  {reconcile?.running
                    ? (isAr ? 'جاري الإصلاح…' : 'Fixing…')
                    : (isAr ? 'إصلاح تلقائي' : 'Auto-fix')}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
